import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useHotkeys } from "react-hotkeys-hook";
import { useEffect } from "@remi/next-ui";

import { useIsMacPlatform } from "~/hooks/useIsMacPlatform";
import { useIsModalOverlayOpen } from "~/hooks/useOverlayState";
import { useUnifiedSidebarEnabled } from "~/hooks/useUnifiedSidebarEnabled";
import { CommandMenuContext } from "./CommandMenuContext";

const CommandMenu = dynamic(
  () =>
    typeof window === "undefined"
      ? Promise.resolve(() => null)
      : // oxlint-disable-next-line remi/no-async-import -- client-only; defers the menu, sidebar, favorites and search hooks from the _app chunk until first open and keeps them out of every server function
        import("~/components/command-menu/CommandMenu").then((m) => m.CommandMenu),
  { ssr: false },
);

const EMBED_PATH_PREFIX = "/embed";

interface CommandMenuProviderProps {
  readonly children: ReactNode;
}

export const CommandMenuProvider = ({ children }: CommandMenuProviderProps) => {
  const router = useRouter();
  const isEnabled = useUnifiedSidebarEnabled() === true && !router.pathname.startsWith(EMBED_PATH_PREFIX);
  const isModalOpen = useIsModalOverlayOpen();
  const isMac = useIsMacPlatform();
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const restoreFocusRef = useRef(true);

  useEffect(() => {
    if (!isEnabled) {
      setIsOpen(false);
    }
  }, [isEnabled]);

  const open = useCallback(() => {
    if (!isEnabled) {
      return;
    }
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    restoreFocusRef.current = true;
    setHasOpened(true);
    setIsOpen(true);
  }, [isEnabled]);

  const closeMenu = useCallback((navigated: boolean) => {
    restoreFocusRef.current = !navigated;
    setIsOpen(false);
  }, []);

  useHotkeys(
    // "mod" also matches Ctrl on a Mac, where Ctrl+K is the text fields' kill-line.
    isMac ? "meta+k" : "ctrl+k",
    (event) => {
      // Not ignoreEventWhen: it returns before preventDefault, so a held Ctrl+K would reach the browser.
      if (event.repeat) {
        return;
      }
      if (isOpen) {
        closeMenu(false);
      } else if (!isModalOpen) {
        open();
      }
    },
    {
      enabled: isEnabled,
      preventDefault: true,
      enableOnFormTags: true,
      // The TipTap composer binds Mod-k to insert-link; it keeps the key there.
      enableOnContentEditable: false,
    },
  );

  // A hotkey-opened dialog has no trigger, so Radix's own focus return would land on <body>.
  const handleCloseAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    const target = returnFocusRef.current;
    if (restoreFocusRef.current && target?.isConnected) {
      target.focus();
    }
  }, []);

  const controls = useMemo(() => ({ open }), [open]);

  return (
    <CommandMenuContext.Provider value={controls}>
      {children}
      {isEnabled && hasOpened && (
        <CommandMenu open={isOpen} onClose={closeMenu} onCloseAutoFocus={handleCloseAutoFocus} />
      )}
    </CommandMenuContext.Provider>
  );
};
